import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchAdviser } from '../api/advisers';
import SiteLayout from '../components/SiteLayout';
import { useLocale } from '../hooks/useLocale';

const appUrl = process.env.REACT_APP_URL;

export default function AdvisorProfilePage() {
  const { t } = useLocale();
  const labels = t.search;
  const { publicId } = useParams();
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    fetchAdviser(publicId)
      .then((adviser) => {
        if (cancelled) return;
        setState({ status: 'ready', adviser });
        const name = [adviser.name, adviser.lastN].filter(Boolean).join(' ');
        document.title = `${name || adviser.username} | Leveltry`;
      })
      .catch((error) => {
        if (cancelled) return;
        setState({
          status: 'error',
          message: error.message || labels.notFound,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [publicId, labels.notFound]);

  const adviser = state.adviser;
  const displayName = adviser
    ? [adviser.name, adviser.lastN].filter(Boolean).join(' ') || adviser.username
    : '';

  return (
    <SiteLayout t={t}>
      <main id="contenido" className="adviser-profile-page">
        {state.status === 'loading' ? (
          <div className="shell adviser-search-page__status">
            <p>{labels.loading}</p>
          </div>
        ) : null}

        {state.status === 'error' ? (
          <div className="shell adviser-search-page__status adviser-search-page__status--error">
            <p>{state.message || labels.notFound}</p>
            <Link to="/buscar" className="btn btn-ghost">
              {labels.backToSearch}
            </Link>
          </div>
        ) : null}

        {state.status === 'ready' ? (
          <>
            <section className="static-page__hero adviser-profile-page__hero">
              <div className="shell adviser-profile-page__hero-inner">
                <div className="adviser-profile-page__avatar">
                  {adviser.image ? (
                    <img src={adviser.image} alt="" />
                  ) : (
                    <span aria-hidden="true">
                      {(adviser.name || '?').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <p className="eyebrow">{labels.profileEyebrow}</p>
                  <h1>
                    {displayName}
                    {adviser.adviserVerified ? (
                      <span className="adviser-badge">{labels.verified}</span>
                    ) : null}
                  </h1>
                  {adviser.expertise ? (
                    <p className="static-page__lead">{adviser.expertise}</p>
                  ) : null}
                  <div className="adviser-card__meta">
                    {adviser.country ? <span>{adviser.country}</span> : null}
                    {adviser.rating != null ? (
                      <span>
                        {labels.rating}: {Number(adviser.rating).toFixed(1)}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </section>

            <section className="shell adviser-profile-page__body">
              {adviser.categories?.length ? (
                <div className="adviser-profile-page__block">
                  <h2>{labels.categories}</h2>
                  <ul className="adviser-chip-list">
                    {adviser.categories.map((cat) => (
                      <li key={cat}>{cat}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {adviser.languages?.length ? (
                <div className="adviser-profile-page__block">
                  <h2>{labels.languages}</h2>
                  <ul className="adviser-chip-list">
                    {adviser.languages.map((lang) => (
                      <li key={lang}>{lang}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {adviser.portfolio?.length ? (
                <div className="adviser-profile-page__block">
                  <h2>{labels.portfolio}</h2>
                  <div className="adviser-portfolio-grid">
                    {adviser.portfolio.map((item) =>
                      item.mediaType === 'video' ? (
                        <video
                          key={item.id}
                          src={item.url}
                          controls
                          playsInline
                          preload="metadata"
                        />
                      ) : (
                        <img key={item.id} src={item.url} alt="" loading="lazy" />
                      )
                    )}
                  </div>
                </div>
              ) : null}

              <div className="adviser-profile-page__cta">
                <h2>{labels.ctaTitle}</h2>
                <p>{labels.ctaCopy}</p>
                <div className="adviser-profile-page__cta-actions">
                  <a
                    href={`${appUrl}/register`}
                    className="btn btn-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {labels.ctaRegister}
                  </a>
                  <a
                    href={`${appUrl}/login`}
                    className="btn btn-ghost"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {labels.ctaLogin}
                  </a>
                </div>
              </div>
            </section>
          </>
        ) : null}
      </main>
    </SiteLayout>
  );
}
